import { Group } from "@entities";
import { Box, Button, Checkbox, FormControlLabel } from "@mui/material";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import { createPhoneNumberFilter, type HyperTableFilter } from "@shared/frontend/hyper-table";
import {
  GROUP_VOLUNTEER_REGION_LABELS,
  GROUP_VOLUNTEER_REGIONS,
  type GroupVolunteerRegion,
} from "@shared/helpers/group-volunteer-regions";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { TeamsSelect } from "../../../components/teams-select";
import { useOrganization } from "../../../contexts/OrganizationProvider";

const filters: HyperTableFilter<Group>[] = [
  { fieldName: "name", id: "name" },
  { fieldName: "description", id: "description" },
  { fieldName: "comments", id: "comments" },
  {
    id: "teams",
    icon: "date",
    caption: "מגיעות בתאריך",
    renderFilter: ({ onSave, meta }) => {
      const dayjsValue =
        typeof meta.value === "string"
          ? dayjs(new Date(meta.value))
          : meta.value;
      return (
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          localeText={
            heIL.components.MuiLocalizationProvider.defaultProps.localeText
          }
          adapterLocale="he"
        >
          <DateCalendar
            // SlotProps={{
            //     leftArrowIcon: { sx: { transform: "rotate(180deg)" } },
            //     rightArrowIcon: { sx: { transform: "rotate(180deg)" } }
            // }}
            value={dayjsValue as Dayjs & { $d: Date }}
            onChange={(value) => {
              onSave({
                value: Group.filterGroupsByEventDate({ date: value }),
                meta: {
                  value,
                },
              });
            }}
          />
        </LocalizationProvider>
      );
    },
  },
  { fieldName: "size", id: "size", caption: "גודל" },
  { fieldName: "type", id: "type", caption: "סוג" },
  { fieldName: "admin.name", id: "adminName", caption: "מוביל קבוצה" },
  createPhoneNumberFilter(Group, {
    fieldName: "admin.phoneNumber",
    caption: "טלפון מוביל קבוצה",
    id: "adminPhoneNumber",
  }),
  {
    id: "projects",
    icon: "literal",
    caption: "פרויקטים",
    renderFilter: ({ onSave, meta }) => {
      const { teams } = meta;
      return (
        <TeamsSelect
          id="optional-groups-teams"
          value={teams as string[]}
          onChange={(nextTeams) =>
            onSave({
              value: nextTeams.length
                ? Group.hyperFilter({
                    team: { $is: { id: { $in: nextTeams as string[] } } },
                  })
                : {},
              meta: { teams: nextTeams },
            })
          }
        />
      );
    },
  },
  {
    id: "preferred-volunteer-regions",
    icon: "literal",
    caption: "אזור התנדבות מועדף",
    renderFilter: ({ onSave, meta }) => {
      const [selectedRegions, setSelectedRegions] = useState<GroupVolunteerRegion[]>(
        (meta.regions as GroupVolunteerRegion[] | undefined) ?? [],
      );
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, p: 1 }}>
          {GROUP_VOLUNTEER_REGIONS.map((region) => (
            <FormControlLabel
              key={region}
              control={
                <Checkbox
                  checked={selectedRegions.includes(region)}
                  onChange={(event) => {
                    setSelectedRegions((prev) =>
                      event.target.checked
                        ? [...prev, region]
                        : prev.filter((item) => item !== region),
                    );
                  }}
                />
              }
              label={GROUP_VOLUNTEER_REGION_LABELS[region]}
            />
          ))}
          <Button
            variant="contained"
            size="small"
            onClick={() =>
              onSave({
                value: selectedRegions.length
                  ? Group.filterByPreferredVolunteerRegions({ regions: selectedRegions })
                  : {},
                meta: { regions: selectedRegions },
              })
            }
          >
            שמירה
          </Button>
        </Box>
      );
    },
  },
  {
    id: "status",
    icon: "literal",
    caption: "סטטוס",
    renderFilter: ({ onSave, meta }) => {
      const { currOrganization } = useOrganization();
      const statuses = (currOrganization?.status || []).filter((status) => status.type === "GROUP");
      const [metaStatuses, setMetaStatuses] = useState<string[]>((meta.statuses as string[]) || []);
      const [noStatus, setNoStatus] = useState<boolean>(!!meta.noStatus);
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, p: 1, maxHeight: 300, overflowY: "auto" }}>
          <FormControlLabel
            control={<Checkbox checked={noStatus} onChange={(event) => setNoStatus(event.target.checked)} />}
            label="ללא סטטוס"
          />
          {statuses.map((status) => (
            <FormControlLabel
              key={status.id}
              control={
                <Checkbox
                  checked={metaStatuses.includes(status.id)}
                  onChange={(event) => {
                    setMetaStatuses((prev) =>
                      event.target.checked
                        ? [...prev, status.id]
                        : prev.filter((id) => id !== status.id),
                    );
                  }}
                />
              }
              label={status.title}
            />
          ))}
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              const conditions = [];
              if (metaStatuses.length) {
                conditions.push({ status: { $is: { id: { $in: metaStatuses } } } });
              }
              if (noStatus) conditions.push({ status: { $is: null } });
              onSave({
                value: conditions.length ? Group.hyperFilter({ $or: conditions }) : {},
                meta: { statuses: metaStatuses, noStatus },
              });
            }}
          >
            שמירה
          </Button>
        </Box>
      );
    },
  },
];

export default filters;
